import type { Campaign } from "./types";

export const printedQrStatuses = ["active", "paused", "retired"] as const;
export type PrintedQrStatus = (typeof printedQrStatuses)[number];

/**
 * A batch of printed material and the permanent code on it (ADR-007). The code
 * is the identity: immutable, case-insensitive, and never reissued. Everything
 * else here is attribution the platform trusts a submitted lead against, so a
 * second or a tenth batch is a record added to this list rather than a change
 * to any validation rule. Kept in step with database/seeds/development.sql.
 */
export type PrintedQrRecord = {
  code: string;
  /** The public path printed before /r/:code existed. Permanent, never reassigned. */
  legacyPath: string;
  campaignId: string;
  campaignSlug: string;
  ambassadorId: string;
  printBatchId: string;
  assetId: string;
  channel: string;
  status: PrintedQrStatus;
};

export const printedQrCodes: PrintedQrRecord[] = [
  {
    code: "K7M2QD4R",
    legacyPath: "/JohnsBUS-518Doors",
    campaignId: "cmp_john_518_doors",
    campaignSlug: "JohnsBUS-518Doors",
    ambassadorId: "amb_john",
    printBatchId: "B001",
    assetId: "ast_john_flyer_v1",
    channel: "qr",
    status: "active",
  },
  {
    code: "T9W5XH3B",
    legacyPath: "/JOESBUS-518Tax",
    campaignId: "cmp_joe_518_tax",
    campaignSlug: "JOESBUS-518Tax",
    ambassadorId: "amb_joe",
    printBatchId: "B001",
    assetId: "ast_joe_flyer_v1",
    channel: "qr",
    status: "active",
  },
];

function printedContract(record: PrintedQrRecord) {
  return {
    /** The permanent opaque route every newly printed code carries. */
    permanentPath: `/r/${record.code}`,
    /** The legacy printed path, still a permanent public contract. */
    pathname: record.legacyPath,
    search: `?amb=${record.ambassadorId}&batch=${record.printBatchId}&asset=${record.assetId}&channel=${record.channel}`,
  };
}

// Printed QR destinations are permanent public contracts. Keep these routes and
// query parameters working for the lifetime of every distributed flyer.
export const printedQrContracts = {
  johnB001: printedContract(printedQrCodes[0]),
  joeB001: printedContract(printedQrCodes[1]),
};

/** Case-insensitive lookup across permanent codes and their legacy paths. */
export function findPrintedQrCode(token: string): PrintedQrRecord | undefined {
  const wanted = decodeURIComponent(token).replace(/^\/+|\/+$/g, "").toLowerCase();
  return printedQrCodes.find(
    (record) =>
      record.code.toLowerCase() === wanted ||
      record.legacyPath.replace(/^\/+/, "").toLowerCase() === wanted,
  );
}

/**
 * Resolves the exact legacy URL shape printed on the original B001 brochures:
 * `/<campaign>/B001`. These paper URLs are permanent public contracts, but an
 * arbitrary second path segment must never be treated as trusted attribution.
 */
export function findPrintedQrByLegacyPath(pathname: string): PrintedQrRecord | undefined {
  let decoded: string;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    return undefined;
  }
  const parts = decoded.replace(/^\/+|\/+$/g, "").split("/");
  if (parts.length !== 2) return undefined;
  const [legacyToken, batch] = parts;
  return printedQrCodes.find(
    (record) =>
      record.status === "active" &&
      record.legacyPath.replace(/^\/+/, "").toLowerCase() === legacyToken.toLowerCase() &&
      record.printBatchId.toLowerCase() === batch.toLowerCase(),
  );
}

/**
 * The trusted record a submitted print attribution must match. Attribution is
 * accepted because a live printed batch exists that says exactly this, not
 * because a particular batch identifier was hardcoded somewhere.
 */
export function matchPrintedQrAttribution(candidate: {
  campaignId: string;
  printBatchId: string;
  ambassadorId?: string;
  channel?: string;
}): PrintedQrRecord | undefined {
  return printedQrCodes.find(
    (record) =>
      record.status === "active" &&
      record.campaignId === candidate.campaignId &&
      record.printBatchId === candidate.printBatchId &&
      record.ambassadorId === candidate.ambassadorId &&
      record.channel === candidate.channel,
  );
}

export const campaigns: Campaign[] = [
  {
    id: "cmp_john_518_doors",
    slug: "JohnsBUS-518Doors",
    partnerId: "ptr_john",
    organizationId: "org_getonbus",
    partnerDisplayName: "John",
    areaCode: "518",
    keyword: "Doors",
    headline: "Open the door to trusted local services.",
    introduction: "Tell us what you need and John’s B.U.S. will connect you with the right local help.",
    partnerProfile: { fullName: "John D’Angelo", professionalRole: "Owner", companyName: "Capital Overhead Doors", companyDescription: "Garage door installation, repair, and maintenance for homes and businesses.", serviceArea: "Capital Region", primaryServiceName: "Garage door installation and repair", primaryServiceDescription: "Installation, repairs, maintenance, and help choosing the right overhead door." },
    services: ["partner-service", "bokeping", "tax", "payroll", "other-service"],
    status: "active",
    fallbackPath: "/services",
    theme: "doors",
    assets: [
      { id: "ast_john_flyer_v1", label: "Approved flyer", kind: "flyer", href: "/assets/johnsbus-518doors-flyer.svg", approved: true },
      { id: "ast_john_card_v1", label: "Contact card", kind: "contact-card", href: "/assets/johnsbus-518doors-contact-card.svg", approved: true },
    ],
  },
  {
    id: "cmp_joe_518_tax",
    slug: "JOESBUS-518Tax",
    partnerId: "ptr_joe",
    organizationId: "org_utaxes",
    partnerDisplayName: "Joe",
    areaCode: "518",
    keyword: "Tax",
    headline: "A clear route to trusted tax help.",
    introduction: "Joe’s B.U.S. connects individuals and small businesses with uTaxes and the right service specialists.",
    partnerProfile: { fullName: "Joseph Printsky", professionalRole: "Tax professional", companyName: "uTaxes", companyDescription: "Tax preparation and payroll support for individuals and small businesses.", serviceArea: "Capital Region", companyLogo: "/assets/utaxes-logo.svg", primaryServiceName: "Tax preparation and payroll", primaryServiceDescription: "Individual and business tax preparation, filing support, and payroll assistance." },
    services: ["tax", "payroll", "bokeping", "other-service"],
    status: "active",
    fallbackPath: "/services",
    theme: "tax",
    assets: [
      { id: "ast_joe_flyer_v1", label: "Approved flyer", kind: "flyer", href: "/assets/joesbus-518tax-flyer.svg", approved: true },
      { id: "ast_joe_card_v1", label: "Contact card", kind: "contact-card", href: "/assets/joesbus-518tax-contact-card.svg", approved: true },
    ],
  },
];

export function findCampaignByPath(pathname: string): Campaign | undefined {
  const printed = findPrintedQrByLegacyPath(pathname);
  if (printed) return campaigns.find((campaign) => campaign.id === printed.campaignId);

  let slug: string;
  try {
    slug = decodeURIComponent(pathname).replace(/^\/+|\/+$/g, "");
  } catch {
    return undefined;
  }
  return campaigns.find((campaign) => campaign.slug.toLowerCase() === slug.toLowerCase());
}
